import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { SEOHead } from '../seo';
import { generateTherapistSEO } from '../../utils/seo-configs';
import HeaderMain from '../homepage/HeaderMain';
import FooterMain from '../homepage/FooterMain';
import {
  ProfileHeader,
  ProfileContent,
  ProfileSidebar
} from '../therapist-profile';

function TherapistProfilePage() {
  const { slug } = useParams();
  const [therapist, setTherapist] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchTherapist();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  const fetchTherapist = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Fetch therapist by slug - only approved profiles will be returned
      const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';
      const response = await fetch(`${API_BASE_URL}/api/therapists/${slug}`);
      
      if (!response.ok) {
        throw new Error('Therapist not found');
      }
      
      const data = await response.json();
      setTherapist(data.therapist);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <>
        <HeaderMain />
        <div className="min-h-screen bg-[#e4fde1] flex items-center justify-center" style={{ marginTop: '-200px', paddingTop: '200px' }}>
          <div className="text-center">
            <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-[#254e70] mx-auto mb-4"></div>
            <p className="text-[#254e70] text-lg">Loading therapist profile...</p>
          </div>
        </div>
        <FooterMain />
      </>
    );
  }

  if (error) {
    return (
      <>
        <HeaderMain />
        <div className="min-h-screen bg-[#e4fde1] flex items-center justify-center" style={{ marginTop: '-200px', paddingTop: '200px' }}>
          <div className="text-center max-w-md mx-auto px-6">
            <div className="text-red-500 text-6xl mb-4">😞</div>
            <h1 className="text-2xl font-bold text-[#254e70] mb-4">Therapist Not Found</h1>
            <p className="text-[#254e70]/70 mb-6">The therapist profile you're looking for doesn't exist or may have been removed.</p>
            <Link 
              to="/find-therapists" 
              className="inline-flex items-center gap-2 bg-[#254e70] text-white px-6 py-3 rounded-lg hover:bg-[#1d3e5a] transition-colors duration-200"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              Find Other Therapists
            </Link>
          </div>
        </div>
        <FooterMain />
      </>
    );
  }

  if (!therapist) return null;

  const seoProps = generateTherapistSEO({
    name: `${therapist.firstName} ${therapist.lastName}`,
    specialty: therapist.specializations?.[0] || 'Mental Health',
    city: therapist.location?.city || 'Your Area',
    bio: therapist.bio,
    slug: therapist.slug || therapist._id,
    profileImage: therapist.profilePhoto
  });

  return (
    <>
      <SEOHead {...seoProps} />
      
      <HeaderMain />
      <main className="bg-[#e4fde1] min-h-screen" style={{ marginTop: '-200px', paddingTop: '200px' }}>
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          
          {/* Header Section */}
          <ProfileHeader therapist={therapist} />

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mt-8">
            {/* Main Content */}
            <div className="lg:col-span-2">
              <ProfileContent therapist={therapist} />
            </div>

            {/* Sidebar */}
            <div className="lg:col-span-1">
              <ProfileSidebar therapist={therapist} />
            </div>
          </div>
        </div>
      </main>
      <FooterMain />
    </>
  );
}

export default TherapistProfilePage; 